import React from 'react'
import PropTypes from 'prop-types'
import { Input, Spin, Alert, Icon } from 'antd'
import ThreadColorSelect from './ThreadColorSelect'
import ChangeReasonBlock from './ChangeReasonBlock'

const COLOR_NAME = { WHITE: 'TRẮNG', BLACK: 'ĐEN', GREY: 'GHI', RED: 'ĐỎ', UNRESOLVED: 'CHƯA XÁC ĐỊNH' }
const COLOR_LABEL = { WHITE: 'WHITE', BLACK: 'BLACK', GREY: 'GREY', RED: 'RED' }

const colorText = (color) => `${COLOR_NAME[color]} · ${COLOR_LABEL[color]}`

const SewingThreadColorPage = ({
    inputValue, loading, error, data, selectedColor, showReasonBlock, submitting,
    inputRef, onChangeInput, onSubmit, onClear,
    onChangeColor, onSelectReason, onSkipReason, onCancelReason,
    onConfirmUseCurrent,
}) => {
    const { item, batch, thread } = data || {}
    const isUnresolved = thread && thread.current === null && thread.printed === 'UNRESOLVED'
    const isMismatch = thread && !isUnresolved && thread.current !== thread.printed
    const isMatch = thread && !isUnresolved && thread.current === thread.printed

    return (
        <div className='SewingThreadColorPage'>
            <div className='container-fluid min-vh-100 py-4'>
                <div className='col-12 d-flex flex-column align-items-center'>
                    <div className='SewingThreadColorContent'>
                        <h2 className='PageTitle mb-3'>Màu chỉ may</h2>

                        <form onSubmit={onSubmit} className='mb-3'>
                            <div className='BarcodeInputWrap'>
                                <Input
                                    size='large'
                                    autoFocus
                                    className='BarcodeInput'
                                    placeholder='Quét QR / barcode item'
                                    value={inputValue}
                                    ref={inputRef}
                                    readOnly={loading}
                                    onChange={e => onChangeInput(e.target.value)}
                                />
                                {!!inputValue && (
                                    <Icon type='close-circle' theme='filled' className='BarcodeInputClear' onClick={onClear} />
                                )}
                            </div>
                        </form>

                        {error && <Alert message={error} type='error' className='mb-3 BarcodeInput' />}

                        <Spin spinning={loading}>
                            {item && (
                                <div className='InfoCard mb-3'>
                                    <div className='InfoCard__title'>
                                        {item.sku} <span className='InfoCard__barcode'>{item.barcode}</span>
                                    </div>
                                    <div className='InfoCard__tags'>
                                        <span className='Tag Tag--status'>{item.production_status}</span>
                                        <span className='Tag'>{item.size}</span>
                                        <span className='Tag'>Lô {batch.name}</span>
                                        {batch.sewing_partner && <span className='Tag'>{batch.sewing_partner.name}</span>}
                                    </div>
                                </div>
                            )}

                            {isMismatch && (
                                <div className='WarningCard mb-3'>
                                    <div className='WarningCard__title'>
                                        <Icon type='warning' theme='filled' /> Mapping đã được cập nhật sau khi item này được in
                                    </div>
                                    <div className='WarningCard__desc'>
                                        QR trên mảnh in <strong>{colorText(thread.printed)}</strong> nhưng màu chỉ hiện hành của thiết kế này là <strong>{colorText(thread.current)}</strong>.
                                    </div>
                                </div>
                            )}

                            {isMatch && (
                                <div className='CurrentColorCard CurrentColorCard--match mb-3'>
                                    <div className='CurrentColorCard__label'>MÀU CHỈ MAY CHỈ ĐỊNH</div>
                                    <div className='CurrentColorCard__value'>{colorText(thread.current)}</div>
                                    <div className='CurrentColorCard__printed'>Khớp với giá trị in trên QR</div>
                                </div>
                            )}

                            {isMismatch && (
                                <div className='CurrentColorCard mb-3'>
                                    <div className='CurrentColorCard__label'>MÀU CHỈ CẦN DÙNG</div>
                                    <div className='CurrentColorCard__value'>{colorText(thread.current)}</div>
                                    <div className='CurrentColorCard__printed'>
                                        QR in: <span className='StrikeThrough'>{colorText(thread.printed)}</span>
                                    </div>
                                </div>
                            )}

                            {isMismatch && (
                                <button type='button' className='ConfirmUseCurrentBtn mb-3' disabled={submitting} onClick={onConfirmUseCurrent}>
                                    ✓ Xác nhận dùng {COLOR_NAME[thread.current]}
                                </button>
                            )}

                            {isMismatch && !showReasonBlock && (
                                <div className='HintText mb-3'>
                                    Một chạm. Thao tác này <strong>không</strong> tạo correction mới — chỉ ghi <code>used_reason</code> = <code>MAPPING_UPDATED</code>.
                                </div>
                            )}

                            {isUnresolved && (
                                <div className='CurrentColorCard CurrentColorCard--unresolved mb-3'>
                                    <div className='CurrentColorCard__label'>MÀU CHỈ MAY CHƯA XÁC ĐỊNH</div>
                                    <div className='CurrentColorCard__value'>?</div>
                                    <div className='CurrentColorCard__forceNote'>
                                        Bắt buộc chọn màu trước khi may
                                    </div>
                                </div>
                            )}

                            {thread && (
                                <ThreadColorSelect
                                    value={selectedColor}
                                    currentColor={thread.current}
                                    allowedColors={thread.allowed_colors}
                                    onChange={onChangeColor}
                                    disabled={submitting}
                                />
                            )}

                            {isMatch && !showReasonBlock && (
                                <div className='HintText mb-3'>
                                    Chạm một màu khác nếu thấy màu chỉ định chưa hợp lý. Tối đa 2 chạm: chọn màu → chọn lý do.
                                </div>
                            )}

                            {showReasonBlock && thread && (
                                <ChangeReasonBlock
                                    currentColor={thread.current}
                                    newColor={selectedColor}
                                    loading={submitting}
                                    onSelectReason={onSelectReason}
                                    onSkipReason={onSkipReason}
                                    onCancel={onCancelReason}
                                />
                            )}
                        </Spin>
                    </div>
                </div>
            </div>
        </div>
    )
}

SewingThreadColorPage.propTypes = {
    inputValue: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    error: PropTypes.string,
    data: PropTypes.object,
    selectedColor: PropTypes.string,
    showReasonBlock: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    inputRef: PropTypes.object.isRequired,
    onChangeInput: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onChangeColor: PropTypes.func.isRequired,
    onSelectReason: PropTypes.func.isRequired,
    onSkipReason: PropTypes.func.isRequired,
    onCancelReason: PropTypes.func.isRequired,
    onConfirmUseCurrent: PropTypes.func.isRequired,
}

export default SewingThreadColorPage
